import React, { useEffect, useState } from "react";
import { SourceTag } from "./SourceTag";
import { centsToInput, toCents } from "./wsUtils";

const base = "w-full text-sm rounded-lg border px-2.5 py-1.5 bg-white focus:outline-none focus:border-brand-400 disabled:bg-slate-50 disabled:text-slate-500";

export function WsField({ label, value, onChange, src, labels, zh, testid, type = "text", placeholder, disabled, hint, rows = 3, options }) {
  const pending = (value === null || value === undefined || value === "") && (!src || src.src === "pending");
  const cls = `${base} ${pending ? "border-red-300 bg-red-50/40" : "border-slate-200"}`;
  return (
    <div data-testid={`${testid}-wrap`} data-pending={pending ? 1 : 0}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <label className="text-[12px] text-slate-600">{label}</label>
        <SourceTag src={pending ? "pending" : src} labels={labels} zh={zh} testid={`${testid}-src`} />
      </div>
      {type === "textarea" ? (
        <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={rows} disabled={disabled} placeholder={placeholder} className={`${cls} resize-y leading-relaxed`} />
      ) : type === "select" ? (
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled} className={cls}>
          <option value="">{placeholder || (zh ? "— 请选择 —" : "— Select —")}</option>
          {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled} placeholder={placeholder || (zh ? "待补全" : "Pending")} className={cls} />
      )}
      {hint && <p className="text-[10.5px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

/** 金额输入: 显示美元, 存整数分; 空 = 待补全 (不写 0)。 */
export function MoneyInput({ value, onChange, testid, disabled, className = "", placeholder }) {
  const [txt, setTxt] = useState(centsToInput(value));
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setTxt(centsToInput(value)); }, [value, focus]);
  const pending = value === null || value === undefined;
  return (
    <div className={`relative ${className}`}>
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-slate-400">$</span>
      <input value={txt} inputMode="decimal" disabled={disabled} data-testid={testid} data-pending={pending ? 1 : 0} placeholder={placeholder || "0.00"}
        onFocus={() => setFocus(true)}
        onBlur={() => { setFocus(false); const c = toCents(txt); onChange(c); setTxt(centsToInput(c)); }}
        onChange={(e) => { setTxt(e.target.value); onChange(toCents(e.target.value)); }}
        className={`w-full text-sm text-right rounded-lg border pl-5 pr-2 py-1.5 bg-white focus:outline-none focus:border-brand-400 disabled:bg-slate-50 tabular-nums ${pending ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} />
    </div>
  );
}

export function NumInput({ value, onChange, testid, disabled, className = "", step = "1", suffix }) {
  const pending = value === null || value === undefined || value === "";
  return (
    <div className={`relative ${className}`}>
      <input type="number" step={step} value={value ?? ""} disabled={disabled} data-testid={testid} data-pending={pending ? 1 : 0}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className={`w-full text-sm text-right rounded-lg border px-2 py-1.5 bg-white focus:outline-none focus:border-brand-400 disabled:bg-slate-50 tabular-nums ${suffix ? "pr-9" : ""} ${pending ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">{suffix}</span>}
    </div>
  );
}

export function Section({ id, title, subtitle, children, badge }) {
  return (
    <section id={`ws-sec-${id}`} data-testid={`ws-section-${id}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 scroll-mt-4">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-[15px] font-bold text-slate-800 tracking-tight">{title}</h3>
          {subtitle && <p className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {badge}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
